/**
 * Сколько копий хранить и каких.
 *
 * Одна последняя копия защищает только от вчерашней беды. Беду замечают
 * позже: испорченную привычку — через неделю, потерянную цель — через месяц, а
 * то, что журнал приёмов за позапрошлый год куда-то делся, — когда его просит
 * врач. Если копия одна, к этому моменту она давно перезаписана порчей.
 *
 * Поэтому копии редеют с возрастом — чем дальше, тем реже:
 *
 * - **семь последних** — всё, что случилось за неделю, можно отыграть по дням;
 * - **двенадцать месячных**: по самой ранней копии текущего месяца и одиннадцати
 *   предыдущих — год в разрешении «месяц»;
 * - **три годовых**: по самой ранней копии каждого из трёх предыдущих лет, то
 *   есть примерно 1 января, — глубина ровно три года.
 *
 * Итого до двадцати двух точек возврата: неделя по дням, год по месяцам, три
 * года по годам. Иногда их двадцать одна — когда первое число месяца попадает
 * и в последнюю неделю: это одна копия в двух ролях, и удваивать её незачем.
 *
 * ## Почему «самая ранняя в периоде», а не «первое число»
 *
 * Копия появляется в тот день, когда человек открыл приложение. Открыл первого
 * марта — копия за первое; уехал на дачу и открыл пятого — самая ранняя за март
 * будет пятого. Требовать именно первое число значит остаться без мартовской
 * копии вовсе, а нужна ближайшая к началу месяца из тех, что есть.
 *
 * ## Почему периоды — окна, а не квоты
 *
 * Считать «двенадцать добавленных месяцев» и «три добавленных года» заманчиво:
 * тогда копий всегда ровно двадцать две. Но глубина при этом плывёт — годовые
 * копии уходят туда, куда не дотянулись месячные, и в январе набор внезапно
 * хранит пять январей вместо трёх. Окно календарное: три года назад — значит
 * три года назад, в любой день года.
 *
 * Текущий год в годовые не входит: он и так представлен месячными копиями, а
 * «глубина три года» — это три года до нынешнего.
 *
 * Функция чистая: на входе список копий, на выходе — что оставить и что убрать.
 * Ни базы, ни текущей даты внутри: окна отсчитываются от самой свежей копии, а
 * не от календаря, — иначе месяц без запусков сдвинул бы весь набор.
 */

import type { IsoDate } from '@/shared/lib/datetime';

export interface RetentionRules {
  /** Сколько последних копий хранить по дням. */
  daily: number;
  /** Сколько месяцев хранить по одной копии. */
  monthly: number;
  /** Сколько лет хранить по одной копии. */
  yearly: number;
}

export const DEFAULT_RETENTION: RetentionRules = { daily: 7, monthly: 12, yearly: 3 };

/** Сколько копий останется при полной истории — предел, не обещание. */
export const RETENTION_TOTAL =
  DEFAULT_RETENTION.daily + DEFAULT_RETENTION.monthly + DEFAULT_RETENTION.yearly;

export interface RetentionPlan<T> {
  keep: T[];
  drop: T[];
}

/**
 * Делит копии на «оставить» и «убрать».
 *
 * Порядок в `keep` — от свежей к старой: в таком виде список и показывают
 * человеку.
 */
export function planRetention<T extends { date: IsoDate }>(
  snapshots: readonly T[],
  rules: RetentionRules = DEFAULT_RETENTION,
): RetentionPlan<T> {
  const sorted = [...snapshots].sort((a, b) => b.date.localeCompare(a.date));
  const newest = sorted[0];
  if (!newest) return { keep: [], drop: [] };

  const keep = new Set<T>(sorted.slice(0, Math.max(0, rules.daily)));

  // Месяцы: текущий и одиннадцать предыдущих.
  for (let back = 0; back < rules.monthly; back += 1) {
    keepEarliestIn(sorted, keep, monthBefore(newest.date, back));
  }

  // Годы: три предыдущих. Текущий сюда не входит — он и так представлен
  // месячными копиями, а «глубина три года» означает три года назад.
  for (let back = 1; back <= rules.yearly; back += 1) {
    keepEarliestIn(sorted, keep, yearBefore(newest.date, back));
  }

  return {
    keep: sorted.filter((snapshot) => keep.has(snapshot)),
    drop: sorted.filter((snapshot) => !keep.has(snapshot)),
  };
}

/** Оставляет самую раннюю копию периода — ближайшую к его началу. */
function keepEarliestIn<T extends { date: IsoDate }>(
  sorted: readonly T[],
  keep: Set<T>,
  prefix: string,
): void {
  // Список отсортирован от свежих к старым, поэтому последняя подходящая —
  // самая ранняя в периоде.
  for (let index = sorted.length - 1; index >= 0; index -= 1) {
    const snapshot = sorted[index]!;
    if (snapshot.date.startsWith(prefix)) {
      keep.add(snapshot);
      return;
    }
  }
}

/** `YYYY-MM` месяца, отстоящего от даты на `back` месяцев назад. */
function monthBefore(date: IsoDate, back: number): string {
  const year = Number(date.slice(0, 4));
  const month = Number(date.slice(5, 7)) - 1 - back;
  const shifted = new Date(Date.UTC(year, month, 1));

  return `${shifted.getUTCFullYear()}-${String(shifted.getUTCMonth() + 1).padStart(2, '0')}`;
}

/** `YYYY` года, отстоящего от даты на `back` лет назад. */
function yearBefore(date: IsoDate, back: number): string {
  return String(Number(date.slice(0, 4)) - back);
}
